import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {L1L3AuthService, UserInfo} from 'ngx-auth-util-lib';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../../environments/local/environment';
import {ERROR_CODES} from '../models/codes';
import {PagedResponse} from '../models/pagedresponse';
import {SharedDetailObject} from '../models/shared-detail-object';
import {
  IInviteUserResponseData,
  InviteUserRequestData,
  IUserMixedDetailDTO,
  IUserMixedListDTO,
  IUserProfileDTO,
  UserFilterParameters
} from '../models/userinfo';
import {map} from "rxjs/operators";
import {ApplicationDTO, ApplicationIds} from "ngx-heka-design-lib";


@Injectable({
  providedIn: 'root'
})
export class UserMgmtService {
  private sharedUser = new SharedDetailObject<IUserMixedDetailDTO>();
  private _userInfoChanged = new BehaviorSubject<UserInfo | null>(null);
  private sharedUserCount = new BehaviorSubject<number>(0);
  private sharedProfile = new BehaviorSubject<IUserProfileDTO | null>(null);

  private pathPrefix = environment.backend.endpoint.host + environment.users.endpoint.paths.prefix


  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private authService: L1L3AuthService
  ) {
    authService.isAuthenticatedSubject.subscribe({
      next: isAuthenticated => {
        if (isAuthenticated) {
          const userInfo = this.authService.getUserInfo();
          this.userInfoChanged.next(userInfo);
        }
      }
    });
  }

  get userInfoChanged(): BehaviorSubject<UserInfo | null> {
    return this._userInfoChanged;
  }

  getApplications(): Observable<ApplicationDTO[]> {
    return this.http.get<ApplicationDTO[]>(this.pathPrefix + environment.users.endpoint.paths.applications)
      .pipe(map(appList => {
        return appList.filter(app => Object.values(ApplicationIds).includes(app.id))
      }));
  }

  public getUsers(userFilterParameters: UserFilterParameters): Observable<PagedResponse<IUserMixedListDTO>> {
    const params = userFilterParameters.generateRequestParametersWithSort(this.sortDataAccessor);
    return this.http.get<PagedResponse<IUserMixedListDTO>>(
      this.pathPrefix, {params}
    );
  }

  public exportUsers(userFilterParameters: UserFilterParameters): Observable<any> {
    const params = userFilterParameters.generateRequestParametersWithSort(this.sortDataAccessor);
    return this.http.get(
      this.pathPrefix + environment.users.endpoint.paths.exports + environment.users.endpoint.paths.csv,
      {
        params,
        responseType: 'arraybuffer'
      }
    );
  }

  public getUserById(id: string): Observable<IUserMixedDetailDTO> {
    return this.http.get<IUserMixedDetailDTO>(environment.backend.endpoint.host + environment.users.endpoint.paths.prefix + `/${id}`);
  }

  public deleteUser(id: string): Observable<any> {
    const sourceTabId = this.authService.sourceTabId;
    let params = new HttpParams();

    if (sourceTabId) {
      params = params.append('sourceTabId', sourceTabId);
    }

    return this.http.delete<any>(
      this.pathPrefix + `/${id}`,
      {params}
    );
  }

  public getOwnUser(): Observable<IUserProfileDTO> {
    return this.http.get<IUserProfileDTO>(
      this.pathPrefix + environment.users.endpoint.paths.profile
    );
  }

  public updateUser(userId: string, roleIds: string[], userGroupIds: string[]): Observable<any> {
    return this.http.put<any>(
      this.pathPrefix + `/${userId}`,
      {roleIds, userGroupIds}
    );
  }

  public inviteUser(inviteUserRequestData: InviteUserRequestData): Observable<IInviteUserResponseData[]> {
    return this.http.post<IInviteUserResponseData[]>(
      this.pathPrefix + environment.users.endpoint.paths.invitations,
      inviteUserRequestData
    );
  }

  public resendUserInvitation(id: string): Observable<any> {
    return this.http.post<any>(
      this.pathPrefix + environment.users.endpoint.paths.invitations +
      `/${id}` + environment.users.endpoint.paths.resend,
      {}
    );
  }

  getSharedUser(userId: string): Observable<IUserMixedDetailDTO | null> {
    if (this.sharedUser.getId() !== userId) {
      this.updateSharedUser(userId);
    }
    return this.sharedUser.getSubject().asObservable();
  }

  updateSharedUser(userId: string): void {
    this.sharedUser.setId(userId);
    this.getUserById(userId).subscribe({
      next: user => {
        this.sharedUser.updateSubject(user);
      },
      error: err => {
        this.sharedUser.reset();
        this.sharedUser.error(err);
      }
    });
  }

  resetSharedUser(): void {
    this.sharedUser.reset();
  }

  getSharedProfile(observableOnly: boolean = false) {
    if (this.sharedProfile.value === null && !observableOnly) {
      this.updateProfile();
    }
    return this.sharedProfile.asObservable();
  }

  resetSharedProfile(): void {
    this.sharedProfile.next(null);
  }

  updateProfile() {
    this.getOwnUser().subscribe({
      next: user => {
        this.sharedProfile.next(user);
      },
      error: err => {
        this.sharedProfile.next(null);
        this.sharedProfile.error(err);
      }
    });
  }

  updateUserCount(users: number): void {
    this.sharedUserCount.next(users);
  }

  getUserCount(): BehaviorSubject<number> {
    return this.sharedUserCount;
  }

  resetUserCount(): void {
    this.sharedUserCount.next(0);
  }

  generateEmailValidationErrorMessage(errorCode: number, isSingularText: boolean): string {
    let message = '';
    switch (errorCode) {
      case ERROR_CODES.USERS_INV_USER_ALREADY_EXIST_IN_SYSTEM:
        if (isSingularText) {
          message = this.translateService.instant('USERS.ERRORS.USERS_INV_USER_ALREADY_EXIST_IN_SYSTEM_SINGULAR');
        } else {
          message = this.translateService.instant('USERS.ERRORS.USERS_INV_USER_ALREADY_EXIST_IN_SYSTEM_PLURAL');
        }
        break;
      case ERROR_CODES.USERS_INV_INVITATION_PENDING:
        if (isSingularText) {
          message = this.translateService.instant('USERS.ERRORS.USERS_INV_INVITATION_PENDING_SINGULAR');
        } else {
          message = this.translateService.instant('USERS.ERRORS.USERS_INV_INVITATION_PENDING_PLURAL');
        }
        break;
      default:
        if (isSingularText) {
          message = this.translateService.instant('USERS.ERRORS.USERS_INVALID_INVITATION_SINGULAR');
        } else {
          message = this.translateService.instant('USERS.ERRORS.USERS_INVALID_INVITATION_PLURAL');
        }
        break;
    }
    return message;
  }

  private sortDataAccessor(property: string): string {
    switch (property) {
      case 'fullName':
        return 'firstName, lastName, email';
      case 'userName':
        return 'username';
      default:
        return property;
    }
  }
}
